import { CRORE, LAKH } from "../model/types.ts";

/** Rupees as crore, e.g. "₹45.9 cr". */
export function crore(v: number, dp = 1): string {
  return `₹${(v / CRORE).toFixed(dp)} cr`;
}

/** Rupees at whatever scale reads best: crore above 1 cr, else lakh. */
export function money(v: number): string {
  const abs = Math.abs(v);
  if (abs >= CRORE) return crore(v, 2);
  if (abs >= LAKH) return `₹${(v / LAKH).toFixed(1)} L`;
  return `₹${Math.round(v).toLocaleString("en-IN")}`;
}

/** Indian digit grouping: 1,23,45,678. */
export function rupees(v: number): string {
  return `₹${Math.round(v).toLocaleString("en-IN")}`;
}

export function pct(v: number, dp = 1): string {
  return `${(v * 100).toFixed(dp)}%`;
}

export function multiple(v: number): string {
  return `${v.toFixed(0)}×`;
}

/** Parse "45", "45L", "1.2cr", "4500000" into rupees. */
export function parseMoney(s: string, unit: "lakh" | "crore" | "rupee" = "lakh"): number {
  const clean = s.replace(/[₹,\s]/g, "").toLowerCase();
  if (!clean) return 0;
  const m = clean.match(/^(-?[\d.]+)(cr|crore|l|lakh|lac)?$/);
  if (!m) return NaN;
  const n = parseFloat(m[1]);
  if (Number.isNaN(n)) return NaN;
  const suffix = m[2];
  if (suffix?.startsWith("cr")) return n * CRORE;
  if (suffix) return n * LAKH;
  return unit === "crore" ? n * CRORE : unit === "lakh" ? n * LAKH : n;
}

/** US dollars with US digit grouping: $1,234,567. */
export function usd(v: number): string {
  const sign = v < 0 ? "−" : "";
  return `${sign}$${Math.round(Math.abs(v)).toLocaleString("en-US")}`;
}

/** Compact dollars for tight spaces: $1.25M, $820K. */
export function usdShort(v: number): string {
  const a = Math.abs(v);
  const sign = v < 0 ? "−" : "";
  if (a >= 1e6) return `${sign}$${(a / 1e6).toFixed(2)}M`;
  if (a >= 1e3) return `${sign}$${Math.round(a / 1e3)}K`;
  return `${sign}$${Math.round(a)}`;
}

export type AmountUnit = "usd" | "inr";

/**
 * Parse what a person types into an amount field. Accepts commas, currency
 * symbols and shorthand: 4.5L, 4.5 lakh, 1.2cr, 250k for rupees; 250k, 1.2M,
 * 1.2mn for dollars. Without a unit, only plain numbers. NaN when unreadable.
 */
export function parseAmount(s: string, unit?: AmountUnit): number {
  const clean = s.replace(/[₹$,\s]/g, "").toLowerCase();
  if (!clean) return NaN;
  const m = clean.match(/^(-?\d*\.?\d+)([a-z]*)$/);
  if (!m) return NaN;
  const n = parseFloat(m[1]);
  const suffix = m[2];
  if (!suffix) return n;
  if (!unit) return NaN;
  if (suffix === "k") return n * 1e3;
  if (unit === "inr") {
    if (["l", "lac", "lakh", "lakhs", "lacs"].includes(suffix)) return n * LAKH;
    if (["cr", "crore", "crores"].includes(suffix)) return n * CRORE;
    return NaN;
  }
  if (["m", "mn", "mm", "million"].includes(suffix)) return n * 1e6;
  if (["b", "bn", "billion"].includes(suffix)) return n * 1e9;
  return NaN;
}

/** Digit grouping in the currency's own convention: 4,50,000 for rupees, 450,000 for dollars. */
export function groupAmount(v: number, unit: AmountUnit): string {
  if (!Number.isFinite(v)) return "";
  const rounded = Math.round(v * 100) / 100;
  return rounded.toLocaleString(unit === "inr" ? "en-IN" : "en-US", { maximumFractionDigits: 2 });
}

/** Amount in words-ish, for the readout under a field: "₹4.5 L", "$1.25M". */
export function amountReadout(v: number, unit: AmountUnit): string {
  return unit === "inr" ? money(v) : usdShort(v);
}
